'use client';

import { Typography } from '@mui/material';
import { BackupStatus, type Condition } from '@repo/api-gw-sdk';
import type { SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { id } from '@/components/queryBuilder/properties';
import { ColumnSelector } from '@/components/table/columnSelector';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { buildPolicyResourcesInventoryCondition } from '@/types/policyResourcesInventoryCondition';

import type { ZeroEtlPolicyEditState } from './ZeroEtlPolicyCreationFlow';
import { ZERO_ETL_SUPPORTED_RESOURCE_TYPES } from './zeroEtlPolicyTypes';

import { InventoryListTable } from '../../inventory/inventoryListTable';

export const ZeroEtlPolicyResourcesList = (
  props: StepProps<ZeroEtlPolicyEditState>
) => {
  const baseCondition = useMemo(() => {
    const backupStatusNotInCondition = createBackupStatusNotInCondition([
      BackupStatus.ExcludedFromBackup,
      BackupStatus.InitialClassification,
      BackupStatus.Terminated,
      BackupStatus.Disconnected,
      BackupStatus.Unsupported,
      BackupStatus.LimitExceeded,
    ]);
    return buildPolicyResourcesInventoryCondition(props.wizardState, {
      resourceTypes: ZERO_ETL_SUPPORTED_RESOURCE_TYPES,
      additionalScopedConditions: [backupStatusNotInCondition],
    });
  }, [props.wizardState]);

  return (
    <StepContainer
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      onBackClick={props.back}
      canGoNext={() => true}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      sx={{ padding: '0px' }}
      navigationComponent={
        <RenameEntityName
          label='Zero-ETL policy name'
          name={props.wizardState.name}
          onSubmit={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
    >
      <Typography className='text-center leading-5 my-[20px]'>
        These are the resources that meet the conditions you set. Review the
        list to make sure it contains the resources you expected.
      </Typography>
      <InventoryTable query={baseCondition} />
    </StepContainer>
  );
};

const InventoryTable = (props: { query?: Condition }) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: id.name, desc: false },
  ]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  return (
    <InventoryListTable
      isMultiSelect={false}
      query={props.query}
      showFiltersBar={false}
      sorting={sorting}
      pagination={pagination}
      onPaginationChange={setPagination}
      onSortingChange={(sortId, desc) => setSorting([{ id: sortId, desc }])}
      paginationBarActions={[ColumnSelector]}
    />
  );
};
